import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import TicketBadge from '../components/TicketBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Calendar, MapPin, Sparkles, Flame, TrendingUp, ChevronRight,
  Search, Bell, Ticket, Loader2, Send, ArrowRight, Music, Beer, Wine,
  PartyPopper, Mic2, Utensils, Briefcase, Star, Gift,
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, eventsItemListLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

const CITIES = [
  { slug: 'denver',       name: 'Denver',       state: 'CO' },
  { slug: 'las-vegas',    name: 'Las Vegas',    state: 'NV' },
  { slug: 'los-angeles',  name: 'Los Angeles',  state: 'CA' },
  { slug: 'charlotte',    name: 'Charlotte',    state: 'NC' },
];

// Map of known event categories to a Lucide icon for visual identity.
// Unknown categories fall back to Sparkles via getCategoryIcon().
const CATEGORY_ICONS = {
  party: PartyPopper,
  festival: Music,
  concert: Music,
  music: Music,
  comedy: Mic2,
  brunch: Utensils,
  food: Utensils,
  tasting: Utensils,
  wine: Wine,
  networking: Briefcase,
  business: Briefcase,
  conference: Briefcase,
  holiday: Gift,
  gala: Star,
};
const getCategoryIcon = (cat) => CATEGORY_ICONS[(cat || '').toLowerCase()] || Sparkles;

// Try IP-based geolocation; fall back to denver. Best-effort only — if the
// user's region matches one of our cities we pre-select it so the page feels
// "local" on first paint.
async function detectCityFromIp() {
  try {
    const r = await axios.get('https://ipapi.co/json/', { timeout: 2500 });
    const region = String(r.data?.city || '').toLowerCase().replace(/\s+/g, '-');
    const hit = CITIES.find((c) => c.slug === region);
    if (hit) return hit.slug;
  } catch (_) { /* ignore */ }
  return null;
}

const formatEventDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const capitalize = (s = '') => String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

export default function FindEvents() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // City can come from EITHER `:city` route param (so /events/denver
  // renders the same rich layout) OR the `?city=` query string used by
  // the legacy /find-events route.
  const routeParams = useParams();
  const routeCity = (routeParams?.city || '').toLowerCase();
  const routeTag = (routeParams?.tag || '').toLowerCase();
  const isCityRoute = Boolean(routeCity);
  const isTagRoute = Boolean(routeTag);
  const initialCity = routeCity || params.get('city') || '';
  const [city, setCity] = useState(initialCity);

  // Keep state in sync if the URL changes (city pill click).
  useEffect(() => {
    if (routeCity && routeCity !== city) setCity(routeCity);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCity]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  // Detect city on first mount if not already chosen
  useEffect(() => {
    if (!initialCity) {
      detectCityFromIp().then((detected) => {
        if (detected) {
          setCity(detected);
          setParams({ city: detected }, { replace: true });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async (citySlug, tagSlug) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/find-events`, {
        params: {
          ...(citySlug ? { city: citySlug } : {}),
          ...(tagSlug ? { tag: tagSlug } : {}),
        },
      });
      setData(r.data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(city, routeTag); }, [city, routeTag, fetchData]);

  // Wine rail — pulls wine events for the current city so the rail can
  // render between Trending Categories and Upcoming. Kept in a separate
  // effect so it doesn't slow the main feed paint.
  const [wineEvents, setWineEvents] = useState([]);
  useEffect(() => {
    if (!city) { setWineEvents([]); return; }
    let cancelled = false;
    axios.get(`${API}/wine-events`, { params: { city, limit: 6 } })
      .then((r) => { if (!cancelled) setWineEvents(r.data?.events || []); })
      .catch(() => { if (!cancelled) setWineEvents([]); });
    return () => { cancelled = true; };
  }, [city]);

  const cityName = useMemo(
    () => CITIES.find((c) => c.slug === city)?.name || (city ? capitalize(city) : 'all cities'),
    [city],
  );

  const handleCityChange = (slug) => {
    setCity(slug);
    // When we're on the /events/:city path, switching cities should
    // navigate to the equivalent /events/<slug> URL (or /find-events
    // when "All cities" picked) so the canonical/SEO stays correct.
    if (isCityRoute) {
      navigate(slug ? `/events/${slug}` : '/find-events');
    } else {
      setParams(slug ? { city: slug } : {});
    }
  };

  const filterByQuery = (list) => {
    const q = query.trim().toLowerCase();
    if (!q) return list || [];
    return (list || []).filter((e) =>
      [e.name, e.tagline, e.short_description, (e.tags || []).join(' '), e.category, e.venue]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  };

  // Canonical & breadcrumb URLs: when we're on /events/{city} use that
  // as the canonical so Google indexes the SEO-friendly path rather
  // than the legacy query-string version.
  const cityPath = city ? `/events/${city}` : '/find-events';
  const canonicalUrl = `${SITE_URL}${isCityRoute || city ? cityPath : '/find-events'}`;
  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Events', url: '/events' },
    ...(city ? [{ name: cityName, url: `/events/${city}` }] : []),
  ]);
  const eventListLD = useMemo(() => {
    const list = (data?.upcoming || []).map((e) => ({
      name: e.name,
      url: e.url,
      slug: e.slug,
      city: e.city,
      event_date: e.event_date,
      event_time: e.event_time,
      image_url: e.image_url,
      venue: e.venue,
      short_description: e.short_description || e.tagline,
    }));
    return list.length > 0
      ? eventsItemListLD(list, `Upcoming events in ${cityName}`, city ? `/events/${city}` : '/find-events')
      : null;
  }, [data?.upcoming, cityName, city]);

  // -----------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="find-events-page">
      <SEO
        noindex={Boolean(city) && city.toLowerCase() !== 'denver'}
        title={
          city
            ? `Find Events in ${cityName} — Concerts, Parties, Festivals | Eventism`
            : 'Find Events Near You — Concerts, Parties, Festivals | Eventism'
        }
        description={
          city
            ? `Discover trending events in ${cityName}: concerts, parties, food experiences, festivals and more. Curated picks, this weekend's hottest tickets, and trending categories.`
            : 'Discover trending events across Denver, Las Vegas, Los Angeles, and Charlotte — curated picks, this weekend\'s hottest tickets, and trending categories.'
        }
        canonical={canonicalUrl}
        url={canonicalUrl}
        keywords={[
          'find events', 'events near me', 'concerts', 'parties', 'festivals',
          city ? `events in ${cityName}` : 'events',
          'this weekend', 'trending events', 'live music',
        ]}
        structuredData={[breadcrumb, eventListLD].filter(Boolean)}
      />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5" data-testid="find-events-hero">
        <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400 flex items-center gap-2">
          <Flame className="h-3.5 w-3.5" /> {isTagRoute ? `#${routeTag.replace(/-/g, ' ')}` : "What's happening"}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-[1.05]" data-testid="find-events-h1">
          {isTagRoute ? (
            <>
              <span className="capitalize">{routeTag.replace(/-/g, ' ')}</span> events in{' '}
              <span className="bg-gradient-to-r from-fuchsia-400 via-amber-400 to-indigo-400 bg-clip-text text-transparent">
                {cityName}
              </span>
              .
            </>
          ) : (
            <>
              Find events in{' '}
              <span className="bg-gradient-to-r from-fuchsia-400 via-amber-400 to-indigo-400 bg-clip-text text-transparent">
                {cityName}
              </span>
              .
            </>
          )}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
          {isTagRoute
            ? `Every event tagged "${routeTag.replace(/-/g, ' ')}" in ${cityName}. Updated daily.`
            : 'Trending parties, concerts, festivals and food experiences — curated by the platform, sorted by what locals are actually buying tickets to.'}
        </p>

        {/* Back to all-events chip when the user landed on a tag page */}
        {isTagRoute && (
          <div className="pt-1">
            <Link
              to={`/events/${city || 'denver'}`}
              className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="find-events-clear-tag"
            >
              ← Clear filter · Browse all events in {cityName}
            </Link>
          </div>
        )}

        {/* City + search bar */}
        <div className="flex flex-col md:flex-row gap-3 pt-2">
          <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="find-events-city-rail">
            <button
              onClick={() => handleCityChange('')}
              className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                !city
                  ? 'border-fuchsia-400 bg-fuchsia-400/10 text-fuchsia-300'
                  : 'border-border hover:border-fuchsia-400/40 text-muted-foreground hover:text-foreground'
              }`}
              data-testid="city-pill-all"
            >
              All cities
            </button>
            {CITIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => handleCityChange(c.slug)}
                className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                  city === c.slug
                    ? 'border-fuchsia-400 bg-fuchsia-400/10 text-fuchsia-300'
                    : 'border-border hover:border-fuchsia-400/40 text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`city-pill-${c.slug}`}
              >
                <MapPin className="h-3 w-3 inline mr-1" /> {c.name}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, vibe, tag…"
              className="pl-9 h-10"
              data-testid="find-events-search"
            />
          </div>
        </div>

        {/* Stat strip */}
        {data?.stats && (
          <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-muted-foreground" data-testid="find-events-stats">
            <span className="flex items-center gap-1">
              <Ticket className="h-3.5 w-3.5 text-amber-400" />
              <strong className="text-foreground">{data.stats.total_upcoming}</strong> upcoming on Eventism
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-indigo-400" />
              <strong className="text-foreground">{data.stats.cities?.length || 0}</strong> active cities
            </span>
            {city && (
              <span className="flex items-center gap-1">
                <Flame className="h-3.5 w-3.5 text-fuchsia-400" />
                <strong className="text-foreground">{data.stats.in_city}</strong> in {cityName}
              </span>
            )}
          </div>
        )}
      </section>

      {/* Loading state */}
      {loading && (
        <div className="py-20 flex justify-center" data-testid="find-events-loading">
          <Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" />
        </div>
      )}

      {/* Empty state — never just show a blank screen */}
      {!loading && data && data.upcoming.length === 0 && data.nearby_other_cities.length === 0 && (
        <NotifyEmptyState city={city} cityName={cityName} />
      )}

      {!loading && data && (
        <>
          {/* FEATURED — large editorial cards */}
          {data.featured?.length > 0 && (
            <Section
              eyebrow="Featured"
              title={`Curators' picks${city ? ` in ${cityName}` : ''}`}
              testId="section-featured"
            >
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filterByQuery(data.featured).map((e) => (
                  <EventCard key={e.event_id} event={e} large />
                ))}
              </div>
            </Section>
          )}

          {/* TRENDING CATEGORIES */}
          {data.trending_categories?.length > 0 && (
            <Section
              eyebrow="Trending categories"
              title={`What's hot${city ? ` in ${cityName}` : ' on Eventism'}`}
              testId="section-categories"
              subhead="Click a category to see every event in that bucket."
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {data.trending_categories.map((c) => {
                  const Icon = getCategoryIcon(c.slug);
                  const tileCity = (city || 'denver').toLowerCase();
                  return (
                    <Link
                      key={c.slug}
                      to={`/events/${tileCity}/tags/${encodeURIComponent(c.slug)}`}
                      className="relative aspect-square rounded-xl overflow-hidden border border-border hover:border-fuchsia-400/50 transition-colors group text-left block"
                      data-testid={`category-tile-${c.slug}`}
                    >
                      {c.image_url ? (
                        <img src={c.image_url} alt={c.label} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity" />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/20 via-amber-500/20 to-indigo-500/20" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 space-y-1">
                        <Icon className="h-5 w-5 text-fuchsia-300" />
                        <p className="font-bold text-white truncate">{c.label}</p>
                        <p className="text-[10px] text-white/70">
                          {c.count} {c.count === 1 ? 'event' : 'events'}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </Section>
          )}

          {/* WINE RAIL — first-class category, deep-linked to /wine/{city} */}
          {wineEvents.length > 0 && (
            <Section
              eyebrow="Wine Events"
              title={city ? `Wine nights in ${cityName}` : 'Wine nights & tastings'}
              testId="section-wine"
              subhead="Vineyard dinners, sommelier flights and varietal showcases — curated by Eventism."
              cta={city ? { href: `/wine/${city}`, label: 'See all wine events' } : { href: '/wine', label: 'See all wine events' }}
            >
              <EventGrid events={filterByQuery(wineEvents)} />
            </Section>
          )}

          {/* THIS WEEKEND */}
          {data.this_weekend?.length > 0 && (
            <Section
              eyebrow="This weekend"
              title={city ? `${cityName} weekend lineup` : 'Weekend lineup'}
              testId="section-this-weekend"
              subhead="Friday → Sunday. Limited tickets — most weekend events sell out by Thursday."
            >
              <EventGrid events={filterByQuery(data.this_weekend)} />
            </Section>
          )}

          {/* TOP TRENDING */}
          {data.top_trending?.length > 0 && (
            <Section
              eyebrow="Trending now"
              title="People are buying these"
              testId="section-trending"
              subhead="Sorted by tickets sold, views, and editorial weight — refreshed every 5 minutes."
            >
              <EventGrid events={filterByQuery(data.top_trending)} showTrendingBadge />
            </Section>
          )}

          {/* THIS WEEK (fallback when no weekend events) */}
          {data.this_weekend?.length === 0 && data.this_week?.length > 0 && (
            <Section
              eyebrow="This week"
              title={`Next 7 days${city ? ` in ${cityName}` : ''}`}
              testId="section-this-week"
            >
              <EventGrid events={filterByQuery(data.this_week)} />
            </Section>
          )}

          {/* ALL UPCOMING */}
          {data.upcoming?.length > 0 && (
            <Section
              eyebrow="All upcoming"
              title={`Every event${city ? ` in ${cityName}` : ''}`}
              testId="section-all-upcoming"
              cta={{ label: 'Browse all events', to: '/events' }}
            >
              <EventGrid events={filterByQuery(data.upcoming)} />
            </Section>
          )}

          {/* NEARBY OTHER CITIES — cold-start safety */}
          {data.nearby_other_cities?.length > 0 && (
            <Section
              eyebrow="Worth the trip"
              title="Popular in other cities"
              testId="section-other-cities"
              subhead={city
                ? `We're growing fast in ${cityName} — meanwhile, here's what's selling out elsewhere.`
                : 'Top events from every Eventism market.'}
            >
              <EventGrid events={filterByQuery(data.nearby_other_cities)} />
            </Section>
          )}

          {/* NOTIFY ME — show whenever the user's chosen city has nothing,
              even if we filled the page with nearby fallback. Empty local
              market = highest-intent moment to capture an email. */}
          {city && data.stats?.in_city === 0 && (
            <NotifyEmptyState city={city} cityName={cityName} />
          )}

          {/* NOTIFY ME — also surface when local has events but is still thin */}
          {city && data.stats?.in_city > 0 && data.stats.in_city < 6 && (
            <NotifyEmptyState city={city} cityName={cityName} compact />
          )}

          {/* CROSS-CTA: organizers funnel */}
          <Section testId="section-organizer-cta" compact>
            <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-fuchsia-500/10 to-indigo-500/10">
              <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-400 flex items-center gap-2">
                    <Ticket className="h-3.5 w-3.5" /> Running your own event?
                  </p>
                  <h3 className="text-2xl font-bold">Sell tickets on Eventism.</h3>
                  <p className="text-sm text-muted-foreground max-w-xl">
                    Lower fees than Eventbrite, marketing built in, and your event will show up right here, ranked by the same trending engine.
                  </p>
                </div>
                <Link to="/sell-tickets" data-testid="find-events-organizer-cta">
                  <Button className="h-11 px-5 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0 shrink-0">
                    <Sparkles className="h-4 w-4 mr-2" /> Get listed <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </Section>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Section wrapper — keeps spacing consistent across the page
// ===========================================================================
function Section({ eyebrow, title, subhead, testId, cta, compact, children }) {
  return (
    <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${compact ? 'mt-10' : 'mt-14'} space-y-5`} data-testid={testId}>
      {(eyebrow || title) && (
        <div className="flex items-end justify-between gap-4">
          <div className="space-y-1">
            {eyebrow && (
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400">
                {eyebrow}
              </p>
            )}
            {title && <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">{title}</h2>}
            {subhead && <p className="text-sm text-muted-foreground max-w-2xl">{subhead}</p>}
          </div>
          {cta && (
            <Link to={cta.to} className="shrink-0">
              <Button variant="ghost" size="sm" className="text-xs">
                {cta.label} <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

// ===========================================================================
// Event grid + card
// ===========================================================================
function EventGrid({ events, showTrendingBadge }) {
  if (!events || events.length === 0) {
    return (
      <Card className="border-dashed border-2 border-border">
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          No events match your search in this section.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {events.map((e) => (
        <EventCard key={e.event_id} event={e} showTrendingBadge={showTrendingBadge} />
      ))}
    </div>
  );
}

function EventCard({ event, large = false, showTrendingBadge = false }) {
  const Icon = getCategoryIcon(event.category);
  return (
    <Link
      to={event.url}
      className="group block"
      data-testid={`event-card-${event.event_id}`}
    >
      <Card className="border-border hover:border-fuchsia-400/50 transition-colors h-full overflow-hidden">
        <div className={`${large ? 'aspect-[16/9]' : 'aspect-video'} bg-card overflow-hidden relative`}>
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={event.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-fuchsia-500/20 via-amber-500/20 to-indigo-500/20 flex items-center justify-center">
              <Icon className="h-10 w-10 text-fuchsia-300" />
            </div>
          )}
          {event.is_featured && (
            <Badge className="absolute top-2 left-2 bg-amber-500 text-black text-[10px] font-bold border-0">
              <Star className="h-3 w-3 mr-1 fill-current" /> Featured
            </Badge>
          )}
          {showTrendingBadge && (event.trending_score || 0) >= 50 && (
            <Badge className="absolute top-2 right-2 bg-fuchsia-500/90 text-white text-[10px] font-bold border-0 backdrop-blur-sm">
              <TrendingUp className="h-3 w-3 mr-1" /> Trending
            </Badge>
          )}
          {event.badge && (
            <div className="absolute bottom-2 left-2">
              <TicketBadge badge={event.badge} size="sm" testId={`event-card-badge-${event.event_id}`} />
            </div>
          )}
        </div>
        <CardContent className="p-3 space-y-1.5">
          <p className="font-bold truncate group-hover:text-fuchsia-300 transition-colors">
            {event.name}
          </p>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 text-fuchsia-400" />
              {formatEventDate(event.event_date)}
            </span>
            {event.city && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {capitalize(event.city)}
              </span>
            )}
          </div>
          {event.tagline && (
            <p className="text-xs text-muted-foreground line-clamp-2">{event.tagline}</p>
          )}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">
              {capitalize(event.category || 'event')}
            </span>
            {event.ticket_price_min != null && event.ticket_price_min > 0 && (
              <span className="text-xs font-semibold text-amber-300">
                From ${event.ticket_price_min}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ===========================================================================
// Empty / notify-me state — cold-start guard
// ===========================================================================
function NotifyEmptyState({ city, cityName, compact = false }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/leads`, {
        client_name: email.split('@')[0],
        client_email: email,
        event_type: 'notify_new_events',
        notes: `Wants new-event alerts for ${cityName}`,
        source: `find-events-notify-${city || 'all'}`,
        city: city || '',
      });
      setSent(true);
      toast.success(`We'll email you when new events drop in ${cityName}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not subscribe — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${compact ? 'mt-12' : 'mt-16'}`} data-testid="find-events-notify">
      <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 via-amber-500/5 to-indigo-500/10">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400 flex items-center gap-2">
              <Bell className="h-3.5 w-3.5" /> Get notified
            </p>
            <h3 className="text-2xl sm:text-3xl font-bold">
              {compact
                ? `More events coming soon to ${cityName}.`
                : `${cityName} is just getting started.`}
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              We add fresh events every week. Drop your email and we'll ping you the moment something new gets listed — no spam, just the good stuff.
            </p>
          </div>
          {sent ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300" data-testid="notify-sent">
              You're on the list for <strong>{cityName}</strong> alerts.
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourdomain.com"
                required
                className="flex-1 h-11"
                data-testid="notify-email"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="h-11 px-6 bg-gradient-to-r from-fuchsia-500 via-amber-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0"
                data-testid="notify-submit-btn"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subscribing…</>
                  : <><Send className="h-4 w-4 mr-2" /> Notify me</>}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
